import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { Member, ObjEntity, ObjRule, PageObjRule, SpaceOut, WatchRelated } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { commentCount } from "../lib/commentCounts.ts";
import { useConversation } from "../lib/conversation.ts";
import { scopeName, useEntities, useMembers } from "../lib/data.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { displayName, nameOf, useSpacePeople } from "../lib/people.ts";
import { both, useAction, useResource } from "../lib/resource.ts";
import { canEditRules } from "../lib/roles.ts";
import { navigate, tabHref, tabParam } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { Person } from "../ui/Avatar.tsx";
import { Button, Link } from "../ui/Button.tsx";
import { CommentCount } from "../ui/counts.tsx";
import { ConfirmButton } from "../ui/Dialog.tsx";
import { Checkbox, Field, Form, Input, Select, Textarea } from "../ui/form.tsx";
import { PlusIcon } from "../ui/icons.tsx";
import { Async, EmptyState, Notice } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { LINK } from "../ui/styles.ts";
import { Table, Td, Th } from "../ui/Table.tsx";
import { TabNav } from "../ui/TabNav.tsx";
import { ConversationLabel, SimpleConversation } from "./comments/conversations.tsx";

interface FormProps {
  space: SpaceOut;
  rule: ObjRule | null;
  entities: readonly ObjEntity[];
  members: readonly Member[];
}

function RuleForm({ space, rule, entities, members }: FormProps) {
  const [name, setName] = useState(rule?.name ?? "");
  const [instructions, setInstructions] = useState(rule?.instructions ?? "");
  const [entity, setEntity] = useState(rule?.entity ?? "");
  const [reviewer, setReviewer] = useState(rule?.reviewer ?? members[0]?.id ?? "");
  const entityName = entities.find((e) => e.id === entity)?.name ?? "";
  const [watch, setWatch] = useState<WatchRelated | "">(rule?.watch_related ?? "");
  const [ignore, setIgnore] = useState<string[]>(rule?.watch_ignore ?? []);
  const [save, saving] = useAction(async () => {
    // Related items are watched for an entity's items (not the triage area's).
    const body = {
      name,
      instructions,
      entity: entity || null,
      reviewer,
      watch_related: (entity && watch) || null,
      watch_ignore: entity && watch ? ignore.filter((e) => e !== entity) : [],
    };
    await (rule
      ? api.patch(spaceApi(space, `/rules/${rule.id}`), { ...body, ver: rule.ver })
      : api.post(spaceApi(space, "/rules"), body));
    toast(t("rules.saved"));
    navigate(spaceUrl(space, "/rules"));
  });
  return (
    <Form onSubmit={save}>
      <fieldset disabled={!canEditRules(space)} class="flex max-w-2xl flex-col gap-4">
        <Field label={t("rules.name")}>
          <Input value={name} onValue={setName} required={true} />
        </Field>
        <Field label={t("rules.instructions")} hint={t("rules.instructionsHint")}>
          <Textarea value={instructions} onValue={setInstructions} rows={6} />
        </Field>
        <Field label={t("rules.scope")}>
          <Select
            value={entity}
            onValue={setEntity}
            options={[
              { value: "", label: t("rules.triage") },
              ...entities.map((e) => ({ value: e.id, label: e.name })),
            ]}
          />
        </Field>
        <Field label={t("rules.reviewer")}>
          <Select
            value={reviewer}
            onValue={setReviewer}
            options={members.map((m) => ({
              value: m.id,
              label: `${displayName(m.id, m.name)} (${t(`userKind.${m.kind}`)})`,
            }))}
          />
        </Field>
        {entity ? (
          <Field label={t("rules.watch")} hint={t(`rules.watch.${watch || "off"}.hint`, { entity: entityName })}>
            <Select
              value={watch}
              onValue={(v) => setWatch(v as WatchRelated | "")}
              options={[
                { value: "", label: t("rules.watch.off") },
                { value: "near", label: t("rules.watch.near") },
                { value: "deep", label: t("rules.watch.deep") },
              ]}
            />
          </Field>
        ) : null}
        {entity && watch ? (
          <fieldset class="flex flex-col gap-1.5">
            <legend class="mb-1 text-sm font-medium text-zinc-800">{t("rules.ignore")}</legend>
            <p class="mb-1 text-xs text-zinc-500">{t("rules.ignoreHint")}</p>
            <div class="flex flex-wrap gap-x-4 gap-y-1.5">
              {entities
                .filter((e) => e.id !== entity)
                .map((e) => (
                  <Checkbox
                    key={e.id}
                    label={e.name}
                    checked={ignore.includes(e.id)}
                    onCheck={(on) => setIgnore(on ? [...ignore, e.id] : ignore.filter((x) => x !== e.id))}
                  />
                ))}
            </div>
          </fieldset>
        ) : null}
        <div class="flex justify-end">
          <Button variant="primary" submit={true} busy={saving} answer="confirm">
            {t("common.save")}
          </Button>
        </div>
      </fieldset>
    </Form>
  );
}

function DeleteRule({ space, rule }: { space: SpaceOut; rule: ObjRule }) {
  const [remove, removing] = useAction(async () => {
    await api.del(spaceApi(space, `/rules/${rule.id}`));
    navigate(spaceUrl(space, "/rules"));
  });
  return (
    <ConfirmButton
      variant="danger"
      label={t("common.delete")}
      title={t("rules.deleteTitle")}
      message={t("rules.deleteMessage")}
      confirmLabel={t("common.delete")}
      busy={removing}
      onConfirm={remove}
    />
  );
}

export function Rules({ space }: PageProps) {
  const list = useResource(`rules:${space.id}`, (signal) =>
    api.get<PageObjRule>(spaceApi(space, "/rules"), undefined, signal).then((p) => p.data),
  );
  const entities = useEntities(space);
  const people = useSpacePeople(space);
  return (
    <>
      <PageHeader
        title={t("rules.title")}
        buttons={
          canEditRules(space)
            ? [
                {
                  key: "new",
                  label: t("rules.new"),
                  icon: <PlusIcon />,
                  symbol: "plus",
                  primary: true,
                  fab: true,
                  href: spaceUrl(space, "/rules/new"),
                },
              ]
            : []
        }
      />
      <Notice when={space.rules_locked}>{t("rules.locked")}</Notice>
      <Async r={list}>
        {(rules) =>
          rules.length === 0 ? (
            <EmptyState title={t("rules.empty")}>{t("rules.emptyHint")}</EmptyState>
          ) : (
            <Table stack={true}>
              <thead class="bg-zinc-50">
                <tr>
                  <Th>{t("rules.name")}</Th>
                  <Th>{t("rules.scope")}</Th>
                  <Th>{t("rules.reviewer")}</Th>
                </tr>
              </thead>
              <tbody class="divide-y divide-zinc-100">
                {rules.map((r) => (
                  <tr key={r.id}>
                    <Td>
                      <Link href={spaceUrl(space, `/rules/${r.id}`)} class={LINK}>
                        {r.name}
                      </Link>
                      <CommentCount
                        count={commentCount(space.id, "rules", r.id)}
                        href={tabHref(spaceUrl(space, `/rules/${r.id}`), "conversation")}
                      />
                    </Td>
                    <Td label={t("rules.scope")}>{scopeName(entities.data, r)}</Td>
                    <Td label={t("rules.reviewer")}>
                      <Person name={nameOf(people, r.reviewer)} id={r.reviewer} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )
        }
      </Async>
    </>
  );
}

/** The rule page's tab from `?tab=`: the rule itself, or its conversation. */
const tabOf = () => tabParam(["rule", "conversation"]);

/** The rule's form (its fields loading first: who reviews, what it's about). */
function RuleFormLoaded({ space, rule }: { space: SpaceOut; rule: ObjRule | null }) {
  const lists = both(useMembers(space), useEntities(space));
  return (
    <>
      <Notice when={space.rules_locked}>{t("rules.locked")}</Notice>
      <Async r={lists}>
        {([members, entities]) => (
          <RuleForm key={rule?.id ?? "new"} space={space} rule={rule} entities={entities} members={members} />
        )}
      </Async>
    </>
  );
}

/** A rule there is: its tabs, the rule (its form) and its conversation. */
function RulePage({ space, rule }: { space: SpaceOut; rule: ObjRule }) {
  const conv = useConversation(space, { rule: rule.id });
  const base = spaceUrl(space, `/rules/${rule.id}`);
  const tab = tabOf();
  return (
    <>
      <TabNav
        label={t("rules.tabs")}
        tabs={[
          { href: tabHref(base, null), label: t("rules.tab.rule"), current: tab === "rule" },
          {
            href: tabHref(base, "conversation"),
            label: <ConversationLabel conv={conv} />,
            current: tab === "conversation",
          },
        ]}
      />
      {tab === "conversation" ? (
        <SimpleConversation space={space} topic={{ rule: rule.id }} conv={conv} />
      ) : (
        <RuleFormLoaded space={space} rule={rule} />
      )}
    </>
  );
}

export function RuleEditor({ space, params }: PageProps) {
  const id = params["id"] ?? "new";
  const r = useResource(`rule:${space.id}:${id}`, (signal) =>
    id === "new" ? Promise.resolve(null) : api.get<ObjRule>(spaceApi(space, `/rules/${id}`), undefined, signal),
  );
  return (
    <Async r={r}>
      {(rule) => (
        <>
          <PageHeader title={rule ? rule.name : t("rules.new")} id={rule?.id}>
            {rule && canEditRules(space) ? <DeleteRule space={space} rule={rule} /> : null}
          </PageHeader>
          {rule ? <RulePage space={space} rule={rule} /> : <RuleFormLoaded space={space} rule={null} />}
        </>
      )}
    </Async>
  );
}
